'use client';
import React, { useRef } from 'react'
import Subheading from '../Subheading/Subheading'
import { LanguageType, LanguageDirection } from '@/lib/types';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from "@gsap/react";
import { debug } from '@/lib/debug';
import { useIsLg } from '@/contexts/BreakpointContext';
gsap.registerPlugin(useGSAP, ScrollTrigger);

type HeadlineType = "long" | "short" | "wide"
type HeadlineAlignment = "left" | "center" | "right"

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
	english: "ltr",
	arabic: "rtl"
};

interface HeadlineProps {
	subheading?: string
	heading: string
	paragraph?: string
	type?: HeadlineType
	alignment?: HeadlineAlignment
	language?: LanguageType
	textColor?: string
	headingSize?: string
	reversedParagraphColor?: boolean
	// Extra classes for the paragraph (e.g. lg:whitespace-pre-line)
	paragraphClassName?: string
	shouldAnimate?: boolean
	useH3?: boolean
}

const ALIGNMENT_TO_TEXT_ALIGN: Record<HeadlineAlignment, string> = {
	"left": "text-left",
	"center": "text-center",
	"right": "text-right",
}

const TYPE_TO_WIDTH: Record<HeadlineType, string> = {
	short: "flex-grow lg:w-[30.6875rem] max-w-[37rem]",
	long: "flex-grow lg:w-[37rem] max-w-[49.6875rem]",
	// Figma Heading Component Size L at its full 997px width. A fluid width
	// with a cap rather than a fixed 997px: a fixed width would set the
	// min-content of any flex column it sits in (e.g. the LP evaluation form),
	// stopping that column from shrinking below it on narrower desktops
	wide: "flex-grow lg:w-full max-w-[62.3125rem]",
}

const Headline: React.FC<HeadlineProps> = ({
	subheading,
	heading,
	paragraph,
	type = "long",
	alignment = "left",
	language = "english",
	textColor = "text-text-primary",
	headingSize = "text-52-to-30--neue-june",
	reversedParagraphColor = false,
	paragraphClassName = '',
	shouldAnimate = true,
	useH3 = false
}) => {
	const headlineRef = useRef<HTMLDivElement>(null);
	const subheadingRef = useRef<HTMLDivElement>(null);
	const headingRef = useRef<HTMLHeadingElement>(null);
	const paragraphRef = useRef<HTMLParagraphElement>(null);
	const isLg = useIsLg();

	// debug.log('heading ', heading);

	useGSAP(() => {
		const headline = headlineRef.current;
		const subheadingEl = subheadingRef.current;
		const headingEl = headingRef.current;
		const paragraphEl = paragraphRef.current;
		if (!headline) return;

		// Collect all elements that exist
		const allElements = [subheadingEl, headingEl, paragraphEl].filter(Boolean);
		if (!allElements.length) return;

		if (shouldAnimate) {
			// Set initial states for all elements
			gsap.set(allElements, {
				autoAlpha: 0,
				filter: 'blur(30px)',
				y: +100,
				force3D: true,
			});

			// Create scroll trigger animation - runs only once
			const tl = gsap.timeline({
				scrollTrigger: {
					trigger: headline,
					start: "bottom bottom",
					toggleActions: "play none none none",
					// markers: true,
				}
			});

			// Animate elements with stagger
			tl.to(allElements, {
				autoAlpha: 1,
				filter: 'blur(0px)',
				y: 0,
				duration: 1.0,
				stagger: 0.2,
				ease: "power2.out",
				force3D: true,
			});
		} else {
			// If animation is disabled, ensure elements are visible
			gsap.set(allElements, {
				autoAlpha: 1,
				filter: 'blur(0px)',
				y: 0,
				force3D: true,
			});
		}

	}, [subheading, heading, paragraph, shouldAnimate]);

	return (
		<div
			ref={headlineRef}
			style={{ direction: "ltr" }}
			className={`
				h-fit
				flex flex-col gap-[1.5rem] flex-shrink
				${TYPE_TO_WIDTH[type]}
				${ALIGNMENT_TO_TEXT_ALIGN[alignment]}
			`}
		>
			<div
				className='flex flex-col gap-[1.25rem] lg:gap-[1.5rem]'>
				{subheading &&
					<div
						ref={subheadingRef}
					>
						<Subheading
							subheading={subheading}
							size={isLg ? 24 : 20}
							alignment={alignment}
							language={language}
							textColor={textColor === "text-text-primary" ? "text-text-quaternary" : textColor} />
					</div>}

				{useH3 ? (
					<h3
						ref={headingRef}
						style={{
							direction: LANGUAGE_TO_DIRECTION[language],
							textWrap: 'balance'
						}}
						className={`
							${headingSize}
							${textColor}
						`}
					>
						{heading}
					</h3>
				) : (
					<h2
						ref={headingRef}
						style={{
							direction: LANGUAGE_TO_DIRECTION[language],
							textWrap: 'balance'
						}}
						className={`
							${headingSize}
							${textColor}
						`}
					>
						{heading}
					</h2>
				)}
			</div>
			{paragraph && <p
				ref={paragraphRef}
				style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
				className={`
					text-18--alexandria
					${reversedParagraphColor ? 'text-text-reversed' : 'text-text-tertiary'}
					${paragraphClassName}
				`}
			>
				{paragraph}
			</p>
			}
		</div >
	);
}

export default Headline;
